import GraphemeSplitter from 'grapheme-splitter';

import { type EmojiId } from '@app/client';
import config from '@app/config';

interface SidebarGroupProps {
    children: Array<SidebarGroup | SidebarYat>;
    name?: string;
    parent?: SidebarGroup;
    open?: boolean;
}

interface SidebarYatInterface {
    emojiId: EmojiId;
    rhythmScore: number;
}

export interface SidebarGroupInterface {
    name?: string;
    children: Array<SidebarYatInterface | SidebarGroupInterface>;
}

type SideBarType = 'SidebarYat' | 'SidebarGroup';

const grapheme = new GraphemeSplitter();

export class SidebarYat {
    type: SideBarType = 'SidebarYat';
    emojiId: EmojiId;
    emojis: string[];
    firstChar?: string;
    trailingChars?: string;
    rhythmScore: number;

    constructor(emojiId, rhythmScore: number) {
        this.emojiId = emojiId;
        this.emojis = grapheme.splitGraphemes(emojiId);
        this.firstChar = this.emojis[0];
        this.trailingChars = grapheme
            .splitGraphemes(emojiId)
            .splice(1, config.maxLengthYat - 1)
            .join('');
        this.rhythmScore = rhythmScore;
    }

    toString(): string {
        return this.emojiId;
    }

    serialize = (): SidebarYat => {
        return { emojiId: this.emojiId, rhythmScore: this.rhythmScore } as SidebarYat;
    };
}

export class SidebarGroup {
    type: SideBarType = 'SidebarGroup';
    name?: string;
    children: Array<SidebarGroup | SidebarYat>;
    parent?: SidebarGroup;
    open?: boolean;

    constructor({ name, children, parent, open = false }: SidebarGroupProps) {
        this.children = children.map((child) => {
            if (child && child.type === 'SidebarYat') {
                return child;
            } else {
                return new SidebarGroup({ parent: this, ...(child as SidebarGroupProps) });
            }
        });
        this.name = name;
        this.parent = parent;
        this.open = open;
    }

    static fromObject(data: SidebarGroupInterface): SidebarGroup {
        const sidebarGroup = new SidebarGroup({
            ...data,
            children: [],
        });
        sidebarGroup.addChildren(data.children);
        return sidebarGroup;
    }

    get length(): number {
        return this.children.length;
    }

    addChildren(children: Array<SidebarYatInterface | SidebarGroupInterface>) {
        this.children = [
            ...this.children,
            ...children.map((c) => {
                if ('emojiId' in c && c.emojiId) {
                    return new SidebarYat(c.emojiId, c.rhythmScore);
                } else {
                    const childGroup = c as SidebarGroupInterface;
                    const sidebarGroup = SidebarGroup.fromObject(childGroup);
                    sidebarGroup.parent = this;
                    return sidebarGroup;
                }
            }),
        ];
    }

    includes(emojiId): boolean {
        return !!this.children.find((child) => {
            if (child && child.type === 'SidebarGroup') {
                return (child as SidebarGroup).includes(emojiId);
            } else {
                return child.toString() === emojiId;
            }
        });
    }

    removeYat(yat: EmojiId) {
        let removeIndex = -1;
        this.children.forEach((child, i) => {
            if (removeIndex > -1) return;
            if ('children' in child) {
                child.removeYat(yat);
            } else {
                if (child.emojiId === yat) {
                    removeIndex = i;
                }
            }
            if (removeIndex > -1) {
                this.children.splice(removeIndex, 1);
                if (this.parent && this.children.length === 0) {
                    // This group is empty, remove it from the paren
                    this.parent.children.splice(this.parent.children.indexOf(this), 1);
                }
            }
        });
    }

    pruneMissing(actualList: Array<EmojiId>) {
        this.yatList.forEach((yat) => {
            if (actualList.indexOf(yat) === -1) {
                // The user no longer owns this Yat, remove it
                this.removeYat(yat);
            }
        });
    }

    get firstChar() {
        if (this.children.length) {
            return this.children[0].firstChar || '';
        }
        return '';
    }

    get firstChars() {
        return this.children.map((c) => (c as SidebarGroup).firstChar);
    }

    get yatList() {
        return this.children.reduce((arr, acc) => {
            const yats = 'emojiId' in acc ? [...new Set([acc.emojiId])] : [...new Set(acc.yatList)];
            return arr.concat(yats);
        }, []);
    }

    get hasOpenSubGroup() {
        return !!this.children.find((d) => {
            if (d && d.constructor && d.constructor === SidebarGroup) {
                return d.open;
            }
            return false;
        });
    }

    closeOpenGroup() {
        const openGroup = this.children.find((d) => {
            if (d.constructor === SidebarGroup && d.open === true) {
                d.open = false;
                return !!d;
            }
            return false;
        });
        if (openGroup) {
            this.children = this.children.map((x) => (x === openGroup ? openGroup : x));
        }
    }

    serialize(): SidebarGroupInterface {
        return {
            name: this.name,
            children: this.children.map((child) => child.serialize()),
        };
    }
}
